// src/auth/RequireAuth.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useAuth } from "./AuthProvider";

/**
 * RequireAuth
 *
 * - Verifică dacă user-ul este autentificat
 * - Opțional: verifică și rolurile necesare pentru ruta curentă
 *
 * Props:
 *   - requiredRoles: array de coduri de rol interne:
 *       ["owner", "director", "reception", "employee"]
 */
export default function RequireAuth({
  children,
  requiredRoles = null,
  requiredFeature = null,
}) {
  const {
    status,
    loading,
    setReturnTo,
    roles,
    roleDetails,
    businesses,
    currentBusinessId,
    permissions,
  } = useAuth();

  const location = useLocation();

  // salvează ruta țintă pentru redirect după login (side-effect corect)
  useEffect(() => {
    if (status === "unauthenticated") {
      const target = location.pathname + location.search + location.hash;
      setReturnTo(target);
    }
  }, [status, location.pathname, location.search, location.hash, setReturnTo]);

  const isSuperAdmin = roles?.includes("Super Administrator");

  // ===== Subscription gate (blocked/canceled -> paywall) =====
  const currentBiz = useMemo(() => {
    const id = currentBusinessId ? String(currentBusinessId) : null;
    return (businesses || []).find((b) => String(b.id) === id) || null;
  }, [businesses, currentBusinessId]);

  // IMPORTANT: dacă avem currentBusinessId dar încă nu avem business-ul în listă,
  // nu randăm pagina (altfel "clipește": se montează dashboard/calendar, face /b/*, apoi redirect).
  const hasBizContext = useMemo(() => {
    if (!currentBusinessId) return true; // în unele flows poate fi null, nu blocăm aici
    if (!Array.isArray(businesses)) return false;
    if (businesses.length === 0) return false;
    return Boolean(currentBiz);
  }, [currentBusinessId, businesses, currentBiz]);

  const subStatus = String(currentBiz?.subscription_status || "").toLowerCase();
  const bizStatus = String(currentBiz?.status || "active").toLowerCase();
  // Dacă admin a setat status="active", respectăm override-ul (chiar dacă Stripe zice past_due).
  // Blocăm doar la blocked/canceled sau dacă business-ul e suspendat explicit de admin.
  const isSuspended =
    bizStatus !== "active" ||
    subStatus === "blocked" ||
    subStatus === "canceled";

  const allowWhileSuspended =
    location.pathname.startsWith("/app/billing") ||
    location.pathname.startsWith("/app/subscription-suspended");

  const effectiveRoleCodes = useMemo(() => {
    const codes = new Set();

    if (isSuperAdmin) {
      codes.add("owner");
      codes.add("director");
      codes.add("reception");
      codes.add("employee");
      return codes;
    }

    if (!roleDetails) return codes;
    const currentBid = currentBusinessId ? Number(currentBusinessId) : null;
    if (!currentBid) return codes;

    (roleDetails.business_roles || []).forEach((br) => {
      if (br.business_id === currentBid && br.role) {
        codes.add(br.role);
      }
    });

    (roleDetails.location_roles || []).forEach((lr) => {
      if (lr.business_id === currentBid && lr.role) {
        codes.add(lr.role);
      }
    });

    return codes;
  }, [isSuperAdmin, roleDetails, currentBusinessId]);

  const hasRequiredRoles = useMemo(() => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (isSuperAdmin) return true;
    return requiredRoles.some((code) => effectiveRoleCodes.has(code));
  }, [requiredRoles, isSuperAdmin, effectiveRoleCodes]);

  const hasRequiredFeature = useMemo(() => {
    if (!requiredFeature) return true;
    if (isSuperAdmin) return true;
    const hasFeatureFn = permissions?.hasFeature;
    if (typeof hasFeatureFn !== "function") return true; // compat
    return Boolean(hasFeatureFn(requiredFeature));
  }, [requiredFeature, isSuperAdmin, permissions]);

  // ===== Render guards (după hooks, ca să nu rupem ordinea) =====
  if (status === "unknown" || loading) {
    return <div className="p-4 text-center">Se verifică sesiunea...</div>;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/app/login" replace />;
  }

  // authenticated, dar încă nu avem contextul de business (evită "blink")
  if (!hasBizContext) {
    return <div className="p-4 text-center">Se încarcă business-ul...</div>;
  }

  // authenticated: dacă e suspendat, îl scoatem din app (except billing + suspended page)
  if (isSuspended && !allowWhileSuspended) {
    return <Navigate to="/app/subscription-suspended" replace />;
  }

  if (!hasRequiredRoles) {
    return (
      <div className="p-6 text-center text-sm text-red-500">
        Nu ai permisiune să accesezi această pagină.
      </div>
    );
  }

  if (!hasRequiredFeature) {
    return (
      <div className="p-6 text-center text-sm text-red-500">
        Funcționalitatea nu este inclusă în planul acestui business.
      </div>
    );
  }

  return children;
}
